import { useEffect, useState } from "react";
import { Star } from "lucide-react";
import { toast } from "sonner";
import SEO from "@/components/site/SEO";
import { Reveal, Eyebrow } from "@/components/site/Reveal";
import { api, fileUrl } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

function Stars({ value, size = 16, onChange }) {
  return (
    <div className="flex gap-1">
      {Array.from({ length: 5 }).map((_, i) => (
        <button
          key={i}
          type={onChange ? "button" : undefined}
          disabled={!onChange}
          onClick={onChange ? () => onChange(i + 1) : undefined}
          data-testid={onChange ? `rating-star-${i + 1}` : undefined}
          className={onChange ? "transition-transform hover:scale-110" : "cursor-default"}
          aria-label={`${i + 1} star`}
        >
          <Star size={size} className={i < value ? "text-teal-primary" : "text-teal-anchor/20"} fill={i < value ? "currentColor" : "none"} />
        </button>
      ))}
    </div>
  );
}

export default function Reviews() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", location: "", project_type: "", body: "", rating: 5 });
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/reviews");
      setReviews(data);
    } catch (e) {
      toast.error("Could not load reviews.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.body) {
      toast.error("Please add your name and review.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/reviews", form);
      toast.success("Thank you! Your review has been posted.");
      setForm({ name: "", location: "", project_type: "", body: "", rating: 5 });
      load();
    } catch (e) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const avg = reviews.length ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1) : "5.0";

  return (
    <div data-testid="reviews-page">
      <SEO title="Customer Reviews" description="Read reviews from Tanonshop customers — homeowners, RV and boat owners who received custom-fit cushion covers." />

      <section className="mx-auto max-w-7xl px-5 sm:px-8 pt-20 pb-14 lg:pt-28">
        <Reveal>
          <Eyebrow>Kind words</Eyebrow>
          <h1 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.03] max-w-3xl">
            Trusted for the perfect fit.
          </h1>
          <div className="mt-6 flex items-center gap-4">
            <Stars value={Math.round(avg)} size={20} />
            <span className="text-lg text-teal-anchor/70">{avg} average · {reviews.length} reviews</span>
          </div>
        </Reveal>
      </section>

      <section className="mx-auto max-w-7xl px-5 sm:px-8 pb-20">
        {loading ? (
          <p className="text-teal-anchor/60">Loading reviews…</p>
        ) : (
          <div className="columns-1 gap-6 sm:columns-2 lg:columns-3 [&>*]:mb-6">
            {reviews.map((r) => (
              <article key={r.id} data-testid="review-card" className="break-inside-avoid border border-teal-anchor/12 bg-white p-7">
                {r.photo && (
                  <div className="mb-5 -mx-7 -mt-7 aspect-[4/3] overflow-hidden">
                    <img src={fileUrl(r.photo)} alt={`${r.name}'s ${r.project_type || "cushion"}`} className="h-full w-full object-cover" />
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <Stars value={r.rating} />
                  {r.date && <span className="text-xs uppercase tracking-[0.12em] text-teal-anchor/40">{r.date}</span>}
                </div>
                <p className="mt-5 text-teal-anchor/80 leading-relaxed">{r.body}</p>
                <div className="mt-6 border-t border-teal-anchor/10 pt-4">
                  <p className="font-serif text-lg tracking-tight">{r.name}</p>
                  <p className="text-xs uppercase tracking-[0.15em] text-teal-primary">
                    {[r.project_type, r.location].filter(Boolean).join(" · ")}
                  </p>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      <section className="bg-paper-soft py-24">
        <div className="mx-auto max-w-2xl px-5 sm:px-8">
          <Reveal>
            <Eyebrow>Worked with us?</Eyebrow>
            <h2 className="mt-4 font-serif text-3xl lg:text-4xl tracking-tight">Leave a review</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <form onSubmit={submit} data-testid="review-form" className="mt-10 space-y-6">
              <div className="grid gap-6 sm:grid-cols-2">
                <div>
                  <Label htmlFor="rv-name" className="text-xs uppercase tracking-[0.15em]">Name *</Label>
                  <Input id="rv-name" data-testid="review-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 rounded-sm border-teal-anchor/20 bg-white" />
                </div>
                <div>
                  <Label htmlFor="rv-loc" className="text-xs uppercase tracking-[0.15em]">Location</Label>
                  <Input id="rv-loc" data-testid="review-location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="mt-2 rounded-sm border-teal-anchor/20 bg-white" />
                </div>
              </div>
              <div>
                <Label htmlFor="rv-proj" className="text-xs uppercase tracking-[0.15em]">Project type</Label>
                <Input id="rv-proj" data-testid="review-project" value={form.project_type} onChange={(e) => setForm({ ...form, project_type: e.target.value })} placeholder="e.g. RV Dinette Cushions" className="mt-2 rounded-sm border-teal-anchor/20 bg-white" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em]">Rating</Label>
                <div className="mt-3"><Stars value={form.rating} size={26} onChange={(r) => setForm({ ...form, rating: r })} /></div>
              </div>
              <div>
                <Label htmlFor="rv-body" className="text-xs uppercase tracking-[0.15em]">Your review *</Label>
                <Textarea id="rv-body" data-testid="review-body" rows={4} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="mt-2 rounded-sm border-teal-anchor/20 bg-white" />
              </div>
              <button type="submit" data-testid="review-submit" disabled={submitting} className="inline-flex rounded-sm bg-teal-primary px-8 py-4 text-xs font-semibold uppercase tracking-[0.18em] text-paper transition-all duration-200 hover:bg-teal-hover hover:-translate-y-px disabled:opacity-60">
                {submitting ? "Posting…" : "Post Review"}
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
